/**
 * PublicLanguageSwitcher — Reusable language toggle for public pages
 * Compact globe + language code dropdown, WCAG compliant
 */
import { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, ChevronDown } from 'lucide-react';
import { SUPPORTED_LANGUAGES } from '../i18n/config';

const PublicLanguageSwitcher = () => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [focusIdx, setFocusIdx] = useState(-1);
  const btnRef = useRef(null);
  const optionRefs = useRef([]);

  const currentLang = SUPPORTED_LANGUAGES.find(l => l.code === i18n.language) || SUPPORTED_LANGUAGES[0];

  const changeLang = (lng) => {
    i18n.changeLanguage(lng);
    setOpen(false);
    btnRef.current?.focus();
  };

  const toggle = useCallback((shouldOpen) => {
    setOpen(shouldOpen);
    if (shouldOpen) {
      const idx = SUPPORTED_LANGUAGES.findIndex(l => l.code === i18n.language);
      setFocusIdx(idx >= 0 ? idx : 0);
      setTimeout(() => optionRefs.current[idx >= 0 ? idx : 0]?.focus(), 0);
    } else {
      setFocusIdx(-1);
      btnRef.current?.focus();
    }
  }, [i18n.language]);

  const handleListKeyDown = useCallback((e) => {
    const count = SUPPORTED_LANGUAGES.length;
    if (e.key === 'Escape') { e.preventDefault(); toggle(false); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); const n = (focusIdx + 1) % count; setFocusIdx(n); optionRefs.current[n]?.focus(); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); const p = (focusIdx - 1 + count) % count; setFocusIdx(p); optionRefs.current[p]?.focus(); }
    else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (focusIdx >= 0) changeLang(SUPPORTED_LANGUAGES[focusIdx].code); }
    else if (e.key === 'Tab') { toggle(false); }
  }, [focusIdx, toggle]);

  return (
    <div className="relative">
      <button
        ref={btnRef}
        id="public-lang-btn"
        onClick={() => toggle(!open)}
        onKeyDown={(e) => { if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); if (!open) toggle(true); } }}
        aria-label={`Select Language (Current: ${currentLang.name})`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls="public-lang-dropdown"
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[#8B95A5] hover:text-white bg-[rgba(255,255,255,0.06)] hover:bg-[rgba(255,255,255,0.1)] transition-colors text-sm"
        data-testid="public-language-switcher-btn"
      >
        <Globe size={14} aria-hidden="true" />
        <span className="font-medium">{currentLang.shortName}</span>
        <ChevronDown size={12} aria-hidden="true" className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => toggle(false)} />
          <ul
            id="public-lang-dropdown"
            role="listbox"
            aria-labelledby="public-lang-btn"
            onKeyDown={handleListKeyDown}
            className="absolute right-0 top-full mt-2 w-44 bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-md shadow-lg z-50 list-none m-0 p-0"
            data-testid="public-language-dropdown"
          >
            {SUPPORTED_LANGUAGES.map((lang, idx) => (
              <li
                key={lang.code}
                ref={el => optionRefs.current[idx] = el}
                role="option"
                tabIndex={-1}
                lang={lang.code}
                aria-selected={i18n.language === lang.code}
                onClick={() => changeLang(lang.code)}
                className={`w-full px-3 py-2.5 flex items-center gap-2.5 text-sm transition-colors cursor-pointer first:rounded-t-md last:rounded-b-md outline-none focus:ring-1 focus:ring-[#38BDF8] focus:ring-inset ${
                  i18n.language === lang.code
                    ? 'bg-[#36A9E1] bg-opacity-15 text-[#38BDF8]'
                    : 'text-[#8B95A5] hover:text-white hover:bg-[rgba(255,255,255,0.04)]'
                }`}
                data-testid={`public-lang-${lang.code}`}
              >
                <span aria-hidden="true">{lang.flag}</span>
                <span>{lang.name}</span>
                {i18n.language === lang.code && (
                  <span className="ml-auto text-[#38BDF8] text-xs font-semibold" aria-hidden="true">&#10003;</span>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};

export default PublicLanguageSwitcher;
